import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, Sparkles, CheckCircle2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { BRAND, IMAGES } from "@/lib/data";

// Kolkata micro-markets we serve. Each has a unique local hook so Google
// treats these as distinct landing pages instead of duplicates.
const AREAS = {
  rajdanga: {
    name: "Rajdanga",
    hook: "Our studio is right here on Rajdanga Main Rd — we've furnished more homes and apartments in and around Rajdanga than any other pocket in Kolkata.",
    landmarks: ["Rajdanga Main Rd", "Gitanjali Stadium", "Sector E, East Kolkata Township", "Ruby Hospital corridor"],
    types: "3BHK apartments, builder-floor duplexes and new-launch towers",
    postal: "700107",
  },
  kasba: {
    name: "Kasba",
    hook: "Kasba's mix of legacy homes and new construction is our sweet spot — modular kitchens, wardrobes and full-home makeovers for growing Kolkata families.",
    landmarks: ["Rashbehari Connector", "Acropolis Mall", "Kasba New Market", "Bosepukur"],
    types: "2 & 3BHK apartments and independent houses",
    postal: "700042 · 700107",
  },
  "salt-lake": {
    name: "Salt Lake",
    hook: "From Sector I bungalows to CK/CL block flats, we deliver premium modular kitchens, walk-in wardrobes and full-home interiors in Salt Lake.",
    landmarks: ["City Centre I", "Salt Lake Stadium", "Sector V"],
    types: "Bungalows, builder flats and Sector V corporate housing",
    postal: "700064 · 700091",
  },
  "new-town": {
    name: "New Town",
    hook: "New Town's high-rise apartments and duplexes deserve interiors that match — ZEBO delivers turnkey home fit-outs across Action Areas I, II & III.",
    landmarks: ["Action Area I", "Eco Park", "Rajarhat", "New Town Rd"],
    types: "High-rise apartments, penthouses and independent villas",
    postal: "700156 · 700160",
  },
  ballygunge: {
    name: "Ballygunge",
    hook: "Ballygunge's heritage-meets-modern apartments call for warm, layered interiors — book-matched marble, brass detailing, and cabinetry that respects tall ceilings.",
    landmarks: ["Gariahat", "Ballygunge Circular Rd", "Southern Ave", "Rashbehari"],
    types: "Heritage flats, luxury towers and independent houses",
    postal: "700019 · 700029",
  },
  garia: {
    name: "Garia",
    hook: "From the Metro stretch to Garia Main Rd, we build sliding-glass wardrobes, compact modular kitchens and light-filled full-home makeovers for South Kolkata families.",
    landmarks: ["Garia Metro", "Garia Main Rd", "Netaji Nagar"],
    types: "2 & 3BHK apartments and builder floors",
    postal: "700084",
  },
};

const SERVICES = [
  { h: "Full-home interiors", b: "One studio for design, factory manufacturing and install — living rooms, bedrooms, puja rooms and beyond." },
  { h: "Modular kitchens", b: "Factory-precise modular kitchens with premium finishes, soft-close hardware and smart storage." },
  { h: "Wardrobes & storage", b: "Bespoke sliding and hinged wardrobes, walk-in closets and space-saving storage designed for your rooms." },
  { h: "Turnkey commercial", b: "Offices, restaurants, cafés and retail — commercial interiors delivered end-to-end, on time." },
];

const REASONS = [
  "Free AI 2D + 360° concepts before you commit",
  "Fixed timelines, itemised BOQ, one project manager",
  "Warranty on modular joinery, factory-precise install",
  "Studio on Rajdanga Main Rd — walk in any weekday",
];

export default function AreaLanding() {
  const { area } = useParams();
  const info = AREAS[area];
  if (!info) return <Navigate to="/" replace />;

  const title = `Interior Designers in ${info.name}, Kolkata — Full-Home, Modular Kitchens & Wardrobes | ZEBO`;
  const desc = `${info.hook} Book a free consultation with ZEBO India Private Limited — the best interior design studio serving ${info.name}, Kolkata.`;
  const path = `/interior-designers-in/${area}`;

  return (
    <main data-testid="area-landing" className="relative z-10">
      <Seo title={title} description={desc} path={path} image={IMAGES.living1} />

      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-end pt-32 pb-16 sm:pb-24 bg-[#143D63] overflow-hidden">
        <img src={IMAGES.living1} alt={`${info.name} Kolkata interiors`} className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/85 via-[#0A0A0A]/40 to-transparent" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 w-full">
          <motion.p initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="overline text-[#F8C21C] mb-4 flex items-center gap-2">
            <MapPin size={14} /> Interior Design in {info.name}, Kolkata
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
            className="font-serif text-5xl sm:text-7xl lg:text-[86px] font-light text-[#F9F8F6] tracking-tight leading-[0.94] max-w-5xl">
            Best interior designers <em className="text-[#F8C21C] not-italic">in {info.name}</em>.
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.5 }}
            className="text-[#F9F8F6]/80 mt-7 max-w-2xl text-base sm:text-lg leading-relaxed">
            {info.hook}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.5 }}
            className="flex flex-wrap gap-3 mt-9">
            <Link to="/consultation" data-testid="area-hero-cta"
              className="group inline-flex items-center gap-2 bg-[#F8C21C] text-[#143D63] px-8 py-4 font-bold text-sm hover:bg-[#F9F8F6] transition-colors">
              Book a Free Consultation
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <Link to="/estimate" data-testid="area-estimate-cta"
              className="inline-flex items-center gap-2 border border-[#F9F8F6]/40 text-[#F9F8F6] px-8 py-4 font-bold text-sm hover:bg-[#F9F8F6]/10 transition-colors">
              <Sparkles size={16} className="text-[#F8C21C]" /> Try Budget Estimator
            </Link>
          </motion.div>
        </div>
      </section>

      {/* What we design */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-28">
        <Reveal>
          <p className="overline text-[#8B0000] mb-3">What we design in {info.name}</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-light text-[#143D63] leading-tight max-w-3xl">
            Every room. Every finish. One accountable studio.
          </h2>
          <p className="text-[#143D63]/70 mt-6 max-w-2xl">
            Local homes across <b>{info.landmarks.join(", ")}</b> ({info.types}). Postal codes: {info.postal}.
          </p>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-px bg-[#143D63]/15 border border-[#143D63]/15 mt-12">
          {SERVICES.map((s, i) => (
            <Reveal key={s.h} delay={i * 0.05} className="bg-[#F9F8F6]">
              <div className="p-8">
                <h3 className="font-serif text-2xl text-[#143D63] mb-3">{s.h}</h3>
                <p className="text-sm text-[#143D63]/70 leading-relaxed">{s.b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Why ZEBO */}
      <section className="bg-[#143D63] py-24 sm:py-28">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-5">
            <p className="overline text-[#F8C21C] mb-3">Why {info.name} chooses ZEBO</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light text-[#F9F8F6] leading-tight">
              Because interiors should feel effortless.
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-6 lg:col-start-7" delay={0.15}>
            <ul className="space-y-4">
              {REASONS.map((r) => (
                <li key={r} className="flex items-start gap-3 text-[#F9F8F6]/85">
                  <CheckCircle2 size={20} className="text-[#F8C21C] mt-0.5 shrink-0" /> {r}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* LocalBusiness schema for this area is emitted statically at build time
          (scripts/prerender_seo.py) — never client-side. */}

      {/* CTA */}
      <section className="py-24 sm:py-28 bg-[#F9F8F6]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 flex flex-col items-center text-center">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl font-light text-[#143D63] leading-tight max-w-3xl">
              Ready to redesign your home in {info.name}?
            </h2>
            <p className="text-[#143D63]/70 mt-6 max-w-xl mx-auto">
              Free consultation, AI concept in a day, a fixed price within a week.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/consultation" data-testid="area-final-cta" className="bg-[#F8C21C] text-[#143D63] px-8 py-4 font-bold text-sm hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors">
                Book Free Consultation
              </Link>
              <Link to="/studio" className="border border-[#143D63] text-[#143D63] px-8 py-4 font-bold text-sm hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors">
                Try AI Studio
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

export const NEIGHBORHOOD_SLUGS = Object.keys(AREAS);
